import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Cpu, Lock, Sparkles, Loader2, PiggyBank } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QueueHistoryCard = () => {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/grading-queue/history?days=7`).then((r) => setHist(r.data)).catch(() => setHist({ points: [] }));
  }, []);
  const points = (hist?.points || []).map((p) => ({
    ...p,
    label: new Date(p.ts).toLocaleString('en-AU', { weekday: 'short', hour: 'numeric' }),
  }));
  const peak = Math.max(0, ...points.map((p) => p.oldest_min));
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 mt-3" data-testid="queue-history-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide">Queue wait — last 7 days</p>
        {hist && <p className="text-[11px] text-white/40" data-testid="queue-history-peak">peak {peak}m {peak > 60 ? '· ⚠ breached 60m' : '· under threshold'}</p>}
      </div>
      {!hist ? <p className="text-white/40 text-sm mt-2">Loading…</p> : points.length === 0 ? (
        <p className="text-white/40 text-sm mt-2">No queue activity in the last 7 days.</p>
      ) : (
        <div className="h-36 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
              <defs>
                <linearGradient id="qwait" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2dd4bf" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#2dd4bf" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} interval={Math.max(1, Math.floor(points.length / 8))} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} axisLine={false} width={54}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k m` : `${v}m`)} />
              <Tooltip
                contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                formatter={(v, name) => [name === 'oldest_min' ? `${v} min` : v, name === 'oldest_min' ? 'Oldest wait' : 'Waiting calls']} />
              <ReferenceLine y={hist.threshold_min || 60} stroke="#f87171" strokeDasharray="4 4" label={{ value: '60m alert', fill: '#f87171', fontSize: 10, position: 'insideTopRight' }} />
              <Area type="monotone" dataKey="oldest_min" stroke="#2dd4bf" strokeWidth={2} fill="url(#qwait)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const OpsStrip = () => {
  const [health, setHealth] = useState(null);
  const [demo, setDemo] = useState(null);
  const [building, setBuilding] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/grading-queue/health`).then((r) => setHealth(r.data)).catch(() => {});
    axios.get(`${API}/admin/demo/status`).then((r) => setDemo(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const buildDemo = async () => {
    setBuilding(true);
    try {
      await axios.post(`${API}/admin/demo/build`);
      toast.success('Demo rebuild started — refreshes in ~1 minute');
      setTimeout(() => { load(); setBuilding(false); }, 60000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setBuilding(false); }
  };

  const [runningQueue, setRunningQueue] = useState(false);
  const runQueueNow = async () => {
    setRunningQueue(true);
    try {
      const r = await axios.post(`${API}/admin/grading-queue/run`);
      const d = r.data;
      toast.success(`Queue run: submitted ${d.submit?.submitted ?? 0}, finalized ${d.poll?.finalized ?? 0}, ${d.poll?.stale ?? 0} stale→inline · ${d.queue?.queued ?? 0} still queued, oldest ${d.oldest_waiting_min}m${d.anthropic_key_present ? '' : ' · ⚠ NO ANTHROPIC KEY'}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Queue run failed'); }
    setRunningQueue(false);
  };

  const genVoices = async () => {
    try {
      await axios.post(`${API}/admin/demo/voice-calls`);
      toast.success('Voice generation started — showcase calls + microsite narrations (~1 min)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const funnel = demo?.tour_funnel || {};
  const stepViews = [0, 1, 2, 3, 4, 5].map((i) => funnel[`step${i}_view`] || 0);
  const finishes = Object.entries(funnel).filter(([k]) => k.endsWith('_finish')).reduce((a, [, v]) => a + v, 0);
  const closes = Object.entries(funnel).filter(([k]) => k.endsWith('_close')).reduce((a, [, v]) => a + v, 0);
  const maxViews = Math.max(...stepViews, 1);
  const hasTourData = stepViews.some((v) => v > 0) || finishes > 0 || closes > 0;

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <div className={`rounded-xl border px-4 py-3 ${health?.alert ? 'border-red-500/50 bg-red-500/10' : 'border-white/10 bg-white/[0.04]'}`} data-testid="queue-health-tile">
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide">Grading queue</p>
        {health ? (
          <>
            <p className="text-xl font-bold text-white mt-1" data-testid="queue-health-summary">
              {health.queued} waiting · {health.submitted} at Anthropic
              {health.oldest_wait_min > 0 && <span className={health.alert ? 'text-red-400' : 'text-white/50'}> · oldest {health.oldest_wait_min}m</span>}
            </p>
            {health.alert && <p className="text-xs text-red-300 font-semibold mt-0.5" data-testid="queue-health-alert">⚠ Calls waiting over an hour — check the submit job / Anthropic status</p>}
            {!health.alert && <p className="text-[11px] text-white/40 mt-0.5">Submits every 15 min · polls every 5 min{health.failed_24h ? ` · ${health.failed_24h} failed (24h)` : ''}</p>}
            <button onClick={runQueueNow} disabled={runningQueue}
              className="mt-1.5 text-[11px] font-semibold text-teal-300 hover:text-teal-200 disabled:opacity-50"
              data-testid="queue-run-now-btn">
              {runningQueue ? 'Running…' : '▶ Run queue now (submit + poll + stale sweep)'}
            </button>
          </>
        ) : <p className="text-white/40 text-sm mt-1">Loading…</p>}
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 flex items-center justify-between gap-3" data-testid="demo-build-tile">
        <div>
          <p className="text-xs font-semibold text-white/50 uppercase tracking-wide">Demo domains</p>
          <p className="text-sm text-white/70 mt-1" data-testid="demo-status-text">
            {demo?.exists ? `Gym & Wellness 360 · ${demo.counts.calls} calls · ${demo.counts.users} users` : 'Not built yet on this environment'}
          </p>
          <p className="text-[11px] text-white/40 mt-0.5" data-testid="demo-verticals-text">
            Car sales: {demo?.verticals?.carsales?.exists ? `${demo.verticals.carsales.calls} calls` : 'not built'} · IT helpdesk: {demo?.verticals?.helpdesk?.exists ? `${demo.verticals.helpdesk.calls} calls` : 'not built'}
          </p>
          <p className="text-[11px] text-white/40 mt-0.5" data-testid="demo-leads-text">
            {demo?.leads?.total ? `${demo.leads.total} demo lead${demo.leads.total === 1 ? '' : 's'} · latest: ${demo.leads.recent[0]?.name} (${demo.leads.recent[0]?.vertical})` : 'No demo leads yet'}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <Button onClick={buildDemo} disabled={building} className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="demo-build-btn">
            {building ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {demo?.exists ? 'Rebuild demo' : 'Build demo'}
          </Button>
          <a href="/admin/demo-leads" className="text-[11px] text-teal-300 hover:text-teal-200 font-semibold text-center" data-testid="demo-leads-link">View leads →</a>
          <button onClick={genVoices} className="text-[11px] text-white/40 hover:text-white/70 font-semibold" data-testid="demo-voices-btn">Generate voices</button>
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3" data-testid="tour-funnel-tile">
        <p className="text-xs font-semibold text-white/50 uppercase tracking-wide">Demo tour funnel</p>
        {hasTourData ? (
          <>
            <div className="flex items-end gap-1.5 mt-2 h-10">
              {stepViews.map((v, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`Step ${i + 1}: ${v} views`}>
                  <span className="text-[9px] text-white/50 leading-none">{v}</span>
                  <div className="w-full bg-teal-400/70 rounded-sm" style={{ height: `${Math.max((v / maxViews) * 28, 2)}px` }} />
                  <span className="text-[9px] text-white/30 leading-none">{i + 1}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-white/40 mt-1.5" data-testid="tour-funnel-summary">
              <span className="text-emerald-400 font-semibold">{finishes} finished</span> · <span className="text-amber-400 font-semibold">{closes} dropped</span>
              {stepViews[0] > 0 && ` · ${Math.round((finishes / stepViews[0]) * 100)}% completion`}
            </p>
          </>
        ) : <p className="text-sm text-white/40 mt-1" data-testid="tour-funnel-empty">No tour activity yet — events log when prospects step through the demo tour</p>}
      </div>
    </div>
  );
};

export default function AdminLlmRoutingPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [advising, setAdvising] = useState(false);

  const load = () => axios.get(`${API}/admin/llm-routing`).then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (lane, model) => {
    setSaving(lane);
    try {
      const r = await axios.patch(`${API}/admin/llm-routing/${lane}`, { model });
      toast.success(`${lane}: ${r.data.previous} → ${model}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    load();
    setSaving(null);
  };

  const askAdvice = async () => {
    setAdvising(true); setAdvice(null);
    try {
      const r = await axios.post(`${API}/admin/llm-routing/advise`, {}, { timeout: 90000 });
      setAdvice(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Advisor failed — try again'); }
    setAdvising(false);
  };

  if (!data) return <AdminLayout><div className="p-6 text-white/40">Loading…</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="llm-routing-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Cpu className="w-6 h-6 text-teal-400" /> LLM Routing
          </h1>
          <Button onClick={askAdvice} disabled={advising} className="bg-teal-600 hover:bg-teal-500 text-white" data-testid="ask-ai-allocation-btn">
            {advising ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Ask AI for best allocation (last 7 days)
          </Button>
        </div>

        <OpsStrip />
        <QueueHistoryCard />

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3" data-testid="grading-locked-card">
          <Lock className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-amber-200">Call Grading — locked to claude-sonnet-4-5</p>
            <p className="text-xs text-amber-200/70 mt-0.5">{data.grading.locked_reason}</p>
          </div>
        </div>

        {data.batch?.enabled && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-start gap-3" data-testid="batch-grading-card">
            <PiggyBank className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-300">Batch API grading is ON — 50% off every grading token</p>
              <p className="text-xs text-emerald-300 mt-0.5">
                Same model, same prompt, temperature 0 — identical scores at half price. Results land within the hour
                (24h worst case); any failure auto-falls back to instant full-price grading so nothing is ever dropped.
              </p>
              <div className="flex gap-4 mt-2 text-xs text-emerald-300 flex-wrap" data-testid="batch-grading-stats">
                <span>Queued: <b>{data.batch.queued}</b></span>
                <span>In flight: <b>{data.batch.in_flight}</b></span>
                <span>Graded (24h): <b>{data.batch.graded_24h}</b></span>
                <span>Failed→inline (24h): <b>{data.batch.failed_24h}</b></span>
                <span className="text-emerald-400 font-semibold">Saved (7d): ~${data.batch.est_saved_7d_usd}</span>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-white/[0.03]">
          <div className="px-4 pt-4 pb-2">
            <p className="text-sm font-semibold text-white">Task lanes</p>
          </div>
          <div className="px-4 pb-4 space-y-2">
            {data.lanes.map((l) => (
              <div key={l.lane} className="flex items-center justify-between gap-3 border border-white/10 rounded-lg px-3 py-2" data-testid={`lane-row-${l.lane}`}>
                <div>
                  <p className="text-sm font-semibold text-white/90">{l.label}</p>
                  <p className="text-[11px] text-white/30">default: {l.default_model}</p>
                </div>
                <select
                  className="border border-white/15 rounded-md text-sm px-2 py-1.5 bg-[#0A0F1C] text-white/90"
                  value={l.current_model}
                  disabled={saving === l.lane}
                  onChange={(e) => save(l.lane, e.target.value)}
                  data-testid={`lane-select-${l.lane}`}
                >
                  {data.allowed_models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            ))}
            <p className="text-[11px] text-white/30">Changes go live within 60 seconds — no redeploy. Every change is audit-logged and emailed to all master admins. The nightly drift canary auto-kills grading on any score drift.</p>
          </div>
        </div>

        {advice && (
          <div className="rounded-xl border border-teal-500/30 bg-white/[0.03]" data-testid="advice-card">
            <div className="px-4 pt-4 pb-2">
              <p className="text-sm font-semibold text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-teal-400" /> AI recommendation</p>
              <p className="text-xs text-white/50 mt-1">{advice.advice.summary}</p>
            </div>
            <div className="px-4 pb-4 space-y-2">
              {(advice.advice.recommendations || []).length === 0 && (
                <p className="text-sm text-white/50 italic">No changes recommended — current allocation already looks optimal.</p>
              )}
              {(advice.advice.recommendations || []).map((r, i) => (
                <div key={i} className="border border-teal-500/20 bg-teal-500/5 rounded-lg px-3 py-2" data-testid={`advice-rec-${r.lane}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm font-semibold text-white/90">{r.lane}: <span className="font-mono">{r.current}</span> → <span className="font-mono text-teal-300">{r.suggested}</span></p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-emerald-400 font-semibold">~${r.est_monthly_saving_usd}/mo</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${r.risk === 'low' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>{r.risk} risk</span>
                      <Button size="sm" variant="outline" className="border-white/20 text-white/80 hover:bg-white/10 bg-transparent" onClick={() => save(r.lane, r.suggested)} data-testid={`apply-advice-${r.lane}`}>Apply</Button>
                    </div>
                  </div>
                  <p className="text-xs text-white/50 mt-1"><span className="font-semibold text-white/70">Why:</span> {r.why}</p>
                </div>
              ))}
              <details className="text-xs text-white/40">
                <summary className="cursor-pointer font-semibold">Evidence used (last 7 days)</summary>
                <pre className="bg-black/30 rounded p-2 mt-1 overflow-auto text-[10px] text-white/60">{JSON.stringify(advice.evidence, null, 1)}</pre>
              </details>
            </div>
          </div>
        )}

        {data.audit?.length > 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <p className="text-sm font-semibold text-white mb-2">Recent changes</p>
            <div className="space-y-1">
              {data.audit.map((a, i) => (
                <p key={i} className="text-xs text-white/40 font-mono">{a.ts?.slice(0, 16)} · {a.lane}: {a.from} → {a.to} · by {a.by}</p>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
